import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Home, User, Users, Calendar, CheckSquare, ArrowRight, AlertCircle } from 'lucide-react';
import { properties } from '@/lib/api';
import type { KanbanProperty } from './types';

interface PropertyCardProps {
  property: KanbanProperty;
  onClick: () => void;
}

// Format price in Icelandic format (e.g., 45.000.000 kr)
function formatPrice(price?: number): string {
  if (!price) return 'Ekkert verð';
  return new Intl.NumberFormat('is-IS').format(price) + ' kr';
}

// Format property type in Icelandic
function formatPropertyType(type: string): string {
  const types: Record<string, string> = {
    'íbúð': 'Íbúð',
    'einbýli': 'Einbýli',
    'raðhús': 'Raðhús',
    'parhús': 'Parhús',
    'sumarhús': 'Sumarhús',
    'atvinnuhúsnæði': 'Atvinnuhúsnæði',
  };
  return types[type] || type;
}

// Calculate completion percentage
function getCompletionPercentage(progress?: { completed: number; total: number }): number {
  if (!progress || progress.total === 0) return 0;
  return Math.round((progress.completed / progress.total) * 100);
}

// Get progress bar color based on completion
function getProgressColor(percentage: number): string {
  if (percentage === 100) return 'bg-green-500';
  if (percentage >= 75) return 'bg-blue-500';
  if (percentage >= 50) return 'bg-yellow-500';
  if (percentage >= 25) return 'bg-orange-500';
  return 'bg-red-500';
}

export function PropertyCard({ property, onClick }: PropertyCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: property.id,
  });

  const completionPercentage = getCompletionPercentage(property.checklist_progress);
  const progressColor = getProgressColor(completionPercentage);

  // Transform style with rotation when dragging (post-it note effect)
  const style = {
    transform: isDragging
      ? `${CSS.Transform.toString(transform)} rotate(2deg)`
      : CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={`
        bg-gradient-to-br from-yellow-50 to-yellow-100
        rounded-lg p-4
        shadow-[3px_3px_8px_rgba(0,0,0,0.15)]
        hover:shadow-[5px_5px_15px_rgba(0,0,0,0.25)]
        transition-all duration-200
        cursor-grab active:cursor-grabbing
        border-l-4 border-yellow-400
        relative
        ${isDragging ? 'scale-105' : 'hover:-translate-y-1'}
      `}
    >
      {/* Property Image (placeholder or actual) */}
      <div className="w-full h-32 bg-gray-200 rounded-md mb-3 overflow-hidden">
        {property.images && property.images.length > 0 ? (
          <img
            src={properties.getImageUrl(property.id, property.images[0])}
            alt={property.address}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <Home className="h-12 w-12 text-gray-400" />
          </div>
        )}
      </div>

      {/* Property Type Badge */}
      <div className="absolute top-3 right-3">
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white/90 backdrop-blur-sm text-gray-700 shadow-sm">
          {formatPropertyType(property.property_type)}
        </span>
      </div>

      {/* Property Address */}
      <div className="mb-3">
        <h3 className="font-bold text-gray-900 text-base leading-tight mb-1">
          {property.address}
        </h3>
        <p className="text-sm text-gray-600">
          {property.postal_code} {property.city}
        </p>
      </div>

      {/* Price & Size */}
      <div className="mb-3 space-y-1">
        <p className="text-lg font-bold text-prima-600">
          {formatPrice(property.price)}
        </p>
        <p className="text-sm text-gray-700">
          {property.size_sqm ? `${property.size_sqm} m²` : ''}
          {property.rooms ? ` • ${property.rooms} herb` : ''}
        </p>
      </div>

      {/* Checklist Progress Bar */}
      {property.checklist_progress && property.checklist_progress.total > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <div className="flex items-center gap-1">
              <CheckSquare className="h-3 w-3 text-gray-600" />
              <span className="font-medium text-gray-700">Framvinda</span>
            </div>
            <span className="font-bold text-gray-900">
              {completionPercentage}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
            <div
              className={`${progressColor} h-full rounded-full transition-all duration-300`}
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
          <p className="text-xs text-gray-600 mt-1">
            {property.checklist_progress.completed} af {property.checklist_progress.total} verkþáttum
          </p>
        </div>
      )}

      {/* Next Action Indicator */}
      {property.next_action && (
        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-start gap-2">
            <ArrowRight className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-blue-900 mb-0.5">Næsta aðgerð:</p>
              <p className="text-xs text-blue-800">{property.next_action}</p>
            </div>
          </div>
        </div>
      )}

      {/* Assignments */}
      <div className="space-y-1.5 mb-3">
        {property.assigned_agent && (
          <div className="flex items-center gap-2 text-xs">
            <User className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
            <span className="text-gray-700 truncate">
              <span className="font-medium">Starfsmaður:</span> {property.assigned_agent.name}
            </span>
          </div>
        )}
        {property.assigned_customer && (
          <div className="flex items-center gap-2 text-xs">
            <Users className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
            <span className="text-gray-700 truncate">
              <span className="font-medium">Viðskiptavinur:</span> {property.assigned_customer.name}
            </span>
          </div>
        )}
      </div>

      {/* Last Activity */}
      <div className="pt-2 border-t border-yellow-300">
        <div className="flex items-center justify-between text-xs text-gray-600">
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            <span>
              {new Date(property.last_activity || property.updated_at || property.created_at)
                .toLocaleDateString('is-IS', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
            </span>
          </div>
          {property.status === 'vandamál' && (
            <div className="flex items-center gap-1 text-red-600">
              <AlertCircle className="h-3.5 w-3.5" />
              <span className="font-medium">Vandamál</span>
            </div>
          )}
        </div>
      </div>

      {/* Post-it note shadow effect (subtle tape at top) */}
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 w-16 h-4 bg-yellow-200/40 rounded-sm" />
    </div>
  );
}
